import { AxiosError } from 'axios';
import api from '@/lib/axios';

export interface DailyTaskAssignee {
  id: string;
  userId: string;
  assignedBy?: string;
  createdAt: string;
}

export interface DailyTaskClient {
  id?: string;
  clientId: string;
  clientName?: string | null;
}

export interface TaskTag {
  id: string;
  name: string;
  color: string;
  icon?: string | null;
}

export type TeamCalendarRecurrencePattern =
  | 'daily'
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'monthly_weekday_1st'
  | 'quarterly'
  | 'yearly';

export interface DailyTaskComment {
  id: string;
  taskId: string;
  userId: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    fullName?: string | null;
    avatarUrl?: string | null;
    avatar_url?: string | null;
  };
  mentions?: Array<{
    id: string;
    userId: string;
    user?: {
      id: string;
      email: string;
      fullName?: string | null;
    } | null;
  }>;
  views?: Array<{
    id: string;
    userId: string;
    viewedAt: string;
    user?: {
      id: string;
      email: string;
      fullName?: string | null;
    } | null;
  }>;
}

export interface DailyTaskFile {
  id: string;
  taskId: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
  storageKey: string;
  uploadedBy: string;
  uploadedAt: string;
  url?: string;
  uploader?: {
    id: string;
    email: string;
  };
}

export interface DailyTaskChangeLogEntry {
  id: string;
  taskId: string;
  tenantId?: string;
  changedBy: {
    id: string;
    email: string;
    fullName?: string | null;
  } | null;
  field: string;
  oldValue: any;
  newValue: any;
  createdAt: string;
}

export type TaskDependencyAnchor = 'FS' | 'SS' | 'FF' | 'SF';

export interface TaskDependency {
  id: string;
  predecessorTaskId: string;
  successorTaskId: string;
  anchorType: TaskDependencyAnchor;
  lagBusinessDays?: number;
  predecessorWorkflowStatus?: string | null;
  predecessorTitle?: string | null;
  successorTitle?: string | null;
  createdAt?: string;
}

export interface CreateTaskDependencyDto {
  predecessorTaskId: string;
  successorTaskId: string;
  anchorType: TaskDependencyAnchor;
  lagBusinessDays?: number;
}

export interface DailyTask {
  id: string;
  userId: string;
  type: 'notion' | 'manual' | 'whatsapp' | 'capture' | 'personal' | 'calendar' | 'tech_system';
  status: 'todo' | 'in_progress' | 'paused' | 'done';
  title: string;
  description?: string;
  workflowStatus?: 'aguardando' | 'em_progresso' | 'bloqueada' | 'suspensa' | 'concluida';
  priority?: 'baixa' | 'media' | 'alta' | 'urgente';
  dueDate?: string | null;
  endDate?: string | null;
  techSystemId?: string | null;
  techSystemName?: string | null;
  projectInstanceId?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  actualStartAt?: string | null;
  actualEndAt?: string | null;
  plannedExecutionMinutes?: number;
  createdBy?: string;
  assignees?: DailyTaskAssignee[];
  clients?: DailyTaskClient[];
  tags?: TaskTag[];
  notionRefId?: string;
  notionLink?: string;
  notionMetadata?: {
    boardName?: string;
    dueDate?: string | { start?: string | null; end?: string | null };
    status?: any;
    boardId?: string;
  };
  estimatedTime?: number; // minutos
  timeSpent: number; // segundos
  logs?: Array<{
    event: 'play' | 'pause' | 'stop';
    timestamp: Date;
  }>;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  xpGained?: number;
  pointsGenerated?: boolean;
  reachedDailyLimit?: boolean;
  xpCounted?: boolean;
  position?: number;
  calendarEventId?: string;
  isTeamCalendar?: boolean;
  teamIds?: string[];
  recurrencePattern?: TeamCalendarRecurrencePattern | null;
  recurrenceGeneratedAt?: string | null;
  recurrenceRootTaskId?: string | null;
  recurrenceOccurrenceKey?: string | null;
  isVirtual?: boolean;
  isRecurrenceSuperseded?: boolean;
  virtualSourceTaskId?: string;
  /** Preenchido em tarefas tipo calendar criadas ao concluir ocorrência do calendário da equipe */
  teamCalendarSourceTaskId?: string | null;
  teamCalendarOccurrenceDueAt?: string | null;
}

export interface CreateTaskDto {
  type: 'notion' | 'manual' | 'whatsapp' | 'capture' | 'personal' | 'calendar' | 'tech_system';
  title: string;
  description?: string;
  workflowStatus?: 'aguardando' | 'em_progresso' | 'bloqueada' | 'suspensa' | 'concluida';
  priority?: 'baixa' | 'media' | 'alta' | 'urgente';
  dueDate?: string | null;
  endDate?: string | null;
  techSystemId?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  actualStartAt?: string | null;
  actualEndAt?: string | null;
  plannedExecutionMinutes?: number;
  assigneeIds?: string[];
  clientIds?: string[];
  tagIds?: string[];
  creationContext?:
    | 'my-dashboard'
    | 'admin-board'
    | 'team-calendar'
    | 'tech-system-project'
    | 'other';
  notionRefId?: string;
  notionLink?: string;
  estimatedTime?: number;
  notionMetadata?: {
    boardName?: string;
    dueDate?: string | { start?: string | null; end?: string | null };
    status?: any;
    boardId?: string;
  };
  calendarEventId?: string;
  isTeamCalendar?: boolean;
  teamIds?: string[];
  recurrencePattern?: TeamCalendarRecurrencePattern;
  recurrenceRootTaskId?: string;
}

export interface UpdateTaskDto {
  title?: string;
  description?: string;
  workflowStatus?: 'aguardando' | 'em_progresso' | 'bloqueada' | 'suspensa' | 'concluida';
  priority?: 'baixa' | 'media' | 'alta' | 'urgente';
  dueDate?: string | null;
  endDate?: string | null;
  techSystemId?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  actualStartAt?: string | null;
  actualEndAt?: string | null;
  plannedExecutionMinutes?: number;
  assigneeIds?: string[];
  clientIds?: string[];
  tagIds?: string[];
  estimatedTime?: number;
  status?: 'todo' | 'in_progress' | 'paused' | 'done';
  teamIds?: string[];
  recurrencePattern?: TeamCalendarRecurrencePattern;
  position?: number;
}

export interface TeamCalendarSettings {
  preAlertDays: number;
  notificationDaysOfWeek: number[];
  /** Filtro global da coluna Próximos prazos (my-dashboard) */
  upcomingDeadlinesTaskTagId: string | null;
}

export interface TeamCalendarTeam {
  id: string;
  name: string;
  description?: string;
  userCount?: number;
  users?: Array<{
    id: string;
    email: string;
    fullName?: string | null;
    active?: boolean;
  }>;
}

export interface CreateCommentDto {
  content: string;
  notifyUserIds?: string[];
}

export interface AdminBoardFilters {
  techSystemId?: string;
  assigneeIds?: string[];
  clientIds?: string[];
  tagIds?: string[];
  dueDateStart?: string;
  dueDateEnd?: string;
  search?: string;
  workflowStatus?: string;
  priority?: string;
  page?: number;
  limit?: number;
}

export interface StartTaskDto {
  estimatedTime: number;
}

export interface TaskFilters {
  status?: string;
  type?: string;
}

export const dailyTasksService = {
  getTasks: async (filters?: TaskFilters): Promise<DailyTask[]> => {
    const { data } = await api.get<DailyTask[]>('/daily-tasks', { params: filters });
    return data;
  },

  getTask: async (id: string): Promise<DailyTask> => {
    const { data } = await api.get<DailyTask>(`/daily-tasks/${id}`);
    return data;
  },

  getTeamCalendarTasks: async (): Promise<DailyTask[]> => {
    const { data } = await api.get<DailyTask[]>('/daily-tasks/team-calendar');
    return Array.isArray(data) ? data : [];
  },

  completeTeamCalendarOccurrence: async (taskId: string): Promise<DailyTask> => {
    const { data } = await api.post<DailyTask>('/daily-tasks/team-calendar/complete', {
      taskId,
    });
    return data;
  },

  getTeamCalendarSettings: async (): Promise<TeamCalendarSettings> => {
    const { data } = await api.get<TeamCalendarSettings>(
      '/daily-tasks/team-calendar/settings',
    );
    return data;
  },

  updateTeamCalendarSettings: async (body: {
    preAlertDays?: number;
    notificationDaysOfWeek?: number[];
    upcomingDeadlinesTaskTagId?: string | null;
  }): Promise<TeamCalendarSettings> => {
    const { data } = await api.patch<TeamCalendarSettings>(
      '/daily-tasks/team-calendar/settings',
      body,
    );
    return data;
  },

  triggerTeamCalendarAlerts: async (): Promise<{ notifiedTasks: number; notifications: number; mattermostMessages: number }> => {
    const { data } = await api.post('/daily-tasks/team-calendar/trigger-alerts');
    return data;
  },

  getTeamCalendarTeams: async (): Promise<TeamCalendarTeam[]> => {
    const { data } = await api.get<TeamCalendarTeam[]>(
      '/daily-tasks/team-calendar/teams',
    );
    return Array.isArray(data) ? data : [];
  },

  createTask: async (dto: CreateTaskDto): Promise<DailyTask> => {
    const { data } = await api.post<DailyTask>('/daily-tasks', dto);
    return data;
  },

  updateTask: async (id: string, dto: UpdateTaskDto): Promise<DailyTask> => {
    const { data } = await api.patch<DailyTask>(`/daily-tasks/${id}`, dto);
    return data;
  },

  startTask: async (id: string, dto: StartTaskDto): Promise<DailyTask> => {
    const { data } = await api.post<DailyTask>(`/daily-tasks/${id}/start`, dto);
    return data;
  },

  pauseTask: async (id: string): Promise<DailyTask> => {
    const { data } = await api.post<DailyTask>(`/daily-tasks/${id}/pause`);
    return data;
  },

  completeTask: async (id: string, timeSpent: number): Promise<DailyTask> => {
    const { data } = await api.post<DailyTask>(`/daily-tasks/${id}/complete`, {
      timeSpent,
    });
    return data;
  },

  updateTimeSpent: async (id: string, timeSpent: number): Promise<DailyTask> => {
    const { data } = await api.patch<DailyTask>(`/daily-tasks/${id}/time-spent`, {
      timeSpent,
    });
    return data;
  },

  deleteTask: async (id: string): Promise<void> => {
    await api.delete(`/daily-tasks/${id}`);
  },

  getTaskHistory: async (params?: {
    page?: number;
    limit?: number;
    type?: string;
    withPointsOnly?: boolean;
  }): Promise<{
    tasks: DailyTask[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> => {
    const { data } = await api.get('/daily-tasks/history', { params });
    return data;
  },

  reorderTasks: async (reorderData: Array<{ id: string; position: number }>): Promise<void> => {
    await api.patch('/daily-tasks/reorder', { reorderData });
  },

  getTasksByEmail: async (email: string, filters?: TaskFilters): Promise<DailyTask[]> => {
    try {
      const { data } = await api.get<DailyTask[]>(
        `/daily-tasks/by-email/${encodeURIComponent(email)}`,
        { params: filters, skipErrorNotification: true },
      );
      return data;
    } catch (e) {
      const err = e as AxiosError;
      if (err.response?.status === 404) return [];
      throw e;
    }
  },

  // Quadro Administrativo
  getTasksForAdminBoard: async (filters?: AdminBoardFilters): Promise<DailyTask[]> => {
    const params: Record<string, string> = {};
    if (filters?.techSystemId) {
      params.techSystemId = filters.techSystemId;
    }
    if (filters?.assigneeIds && filters.assigneeIds.length > 0) {
      params.assigneeIds = filters.assigneeIds.join(',');
    }
    if (filters?.clientIds && filters.clientIds.length > 0) {
      params.clientIds = filters.clientIds.join(',');
    }
    if (filters?.tagIds && filters.tagIds.length > 0) {
      params.tagIds = filters.tagIds.join(',');
    }
    if (filters?.dueDateStart) params.dueDateStart = filters.dueDateStart;
    if (filters?.dueDateEnd) params.dueDateEnd = filters.dueDateEnd;
    if (filters?.search) params.search = filters.search;
    if (filters?.workflowStatus) params.workflowStatus = filters.workflowStatus;
    if (filters?.priority) params.priority = filters.priority;
    if (filters?.page) params.page = String(filters.page);
    if (filters?.limit) params.limit = String(filters.limit);

    const { data } = await api.get<DailyTask[]>('/daily-tasks/admin-board', { params });
    return data;
  },

  // Retorna todas as colunas do kanban em uma única chamada (substitui 5 requests paralelos)
  getAdminBoardAllStatuses: async (
    filters?: Omit<AdminBoardFilters, 'workflowStatus' | 'page'>,
  ): Promise<Record<string, DailyTask[]>> => {
    const params: Record<string, string> = {};
    if (filters?.techSystemId) params.techSystemId = filters.techSystemId;
    if (filters?.assigneeIds?.length) params.assigneeIds = filters.assigneeIds.join(',');
    if (filters?.clientIds?.length) params.clientIds = filters.clientIds.join(',');
    if (filters?.tagIds?.length) params.tagIds = filters.tagIds.join(',');
    if (filters?.search) params.search = filters.search;
    if (filters?.priority) params.priority = filters.priority;
    if (filters?.limit) params.limit = String(filters.limit);

    const { data } = await api.get<Record<string, DailyTask[]>>(
      '/daily-tasks/admin-board/all-statuses',
      { params },
    );
    return data;
  },

  createTaskDependency: async (
    dto: CreateTaskDependencyDto,
  ): Promise<TaskDependency> => {
    const { data } = await api.post<TaskDependency>(
      '/daily-tasks/dependencies',
      dto,
    );
    return data;
  },

  deleteTaskDependency: async (dependencyId: string): Promise<void> => {
    await api.delete(`/daily-tasks/dependencies/${dependencyId}`);
  },

  // Comentários
  createComment: async (taskId: string, dto: CreateCommentDto): Promise<DailyTaskComment> => {
    const { data } = await api.post<DailyTaskComment>(`/daily-tasks/${taskId}/comments`, dto);
    return data;
  },

  getComments: async (
    taskId: string,
    opts?: { limit?: number; beforeCreatedAt?: string },
  ): Promise<{ comments: DailyTaskComment[]; hasMore: boolean }> => {
    const params = new URLSearchParams();
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    if (opts?.beforeCreatedAt) params.set('beforeCreatedAt', opts.beforeCreatedAt);
    const qs = params.toString();
    const { data } = await api.get<{ comments?: DailyTaskComment[]; hasMore?: boolean } | DailyTaskComment[]>(
      `/daily-tasks/${taskId}/comments${qs ? `?${qs}` : ''}`,
    );
    const comments = Array.isArray(data)
      ? data
      : (Array.isArray(data?.comments) ? data.comments : []);
    const hasMore = Array.isArray(data) ? false : (data?.hasMore ?? false);
    return { comments, hasMore };
  },

  markCommentViewed: async (commentId: string): Promise<{ success: boolean }> => {
    const { data } = await api.post<{ success: boolean }>(
      `/daily-tasks/comments/${commentId}/view`,
    );
    return data;
  },

  // Arquivos
  uploadFile: async (taskId: string, file: File): Promise<DailyTaskFile> => {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post<DailyTaskFile>(`/daily-tasks/${taskId}/files`, formData, {
      transformRequest: [
        (data: unknown, headers: Record<string, string>) => {
          delete headers['Content-Type'];
          return data;
        },
      ],
    });
    return data;
  },

  getFiles: async (taskId: string): Promise<DailyTaskFile[]> => {
    const { data } = await api.get<DailyTaskFile[]>(`/daily-tasks/${taskId}/files`);
    return data;
  },

  deleteFile: async (taskId: string, fileId: string): Promise<void> => {
    await api.delete(`/daily-tasks/${taskId}/files/${fileId}`);
  },

  // Changelog / histórico de alterações
  getChangeLogs: async (
    taskId: string,
    params?: { page?: number; limit?: number },
  ): Promise<{ entries: DailyTaskChangeLogEntry[]; hasMore: boolean }> => {
    const { data } = await api.get<{
      entries?: DailyTaskChangeLogEntry[];
      hasMore?: boolean;
    } | DailyTaskChangeLogEntry[]>(`/daily-tasks/${taskId}/change-logs`, {
      params,
    });

    if (Array.isArray(data)) {
      return { entries: data, hasMore: false };
    }

    return {
      entries: Array.isArray(data?.entries) ? data.entries : [],
      hasMore: data?.hasMore ?? false,
    };
  },
};
